import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "./ProductCard";
import ProductQuickView from "./ProductQuickView";
import RequestQuoteModal from "./RequestQuoteModal";
import { Input } from "./ui/input";
import { Search, SlidersHorizontal, ChevronRight, X } from "lucide-react";
import { STOCK_BADGES } from "../constants/catalog";

const STOCK_OPTIONS = Object.keys(STOCK_BADGES);

/**
 * Shared catalog listing used by both Tools and Window Film subcategory pages.
 * Handles fetch, search, filters, breadcrumb, quick view and quote requests.
 */
export default function CatalogListing({ topCategory, topLabel, topPath, category }) {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [brand, setBrand] = useState("");
    const [stock, setStock] = useState("");
    const [type, setType] = useState("");
    const [recommended, setRecommended] = useState(false);
    const [maxPrice, setMaxPrice] = useState("");
    const [sort, setSort] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [quickView, setQuickView] = useState(null);
    const [quoteFor, setQuoteFor] = useState(null);

    useEffect(() => {
        setLoading(true);
        api.get(`/products?category=${topCategory}&subcategory=${category.slug}`)
            .then((r) => setProducts(r.data))
            .finally(() => setLoading(false));
    }, [topCategory, category.slug]);

    const brands = useMemo(() => [...new Set(products.map((p) => p.brand).filter(Boolean))].sort(), [products]);
    const types = useMemo(() => [...new Set(products.map((p) => p.product_type).filter(Boolean))].sort(), [products]);

    const filtered = useMemo(() => {
        let out = products.filter((p) => {
            if (q && !(`${p.name} ${p.description} ${p.mbg_sku}`.toLowerCase().includes(q.toLowerCase()))) return false;
            if (brand && p.brand !== brand) return false;
            if (stock && p.stock_status !== stock) return false;
            if (type && p.product_type !== type) return false;
            if (recommended && !p.mbg_recommended) return false;
            if (maxPrice && Number(p.price) > Number(maxPrice)) return false;
            return true;
        });
        if (sort === "price_asc") out = [...out].sort((a, b) => a.price - b.price);
        if (sort === "price_desc") out = [...out].sort((a, b) => b.price - a.price);
        if (sort === "name") out = [...out].sort((a, b) => a.name.localeCompare(b.name));
        return out;
    }, [products, q, brand, stock, type, recommended, maxPrice, sort]);

    const clearAll = () => { setQ(""); setBrand(""); setStock(""); setType(""); setRecommended(false); setMaxPrice(""); setSort(""); };

    const filterBody = (
        <div className="space-y-7" data-testid="catalog-filters">
            <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#6B645C] mb-2">Search</div>
                <div className="relative">
                    <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B645C]" />
                    <Input data-testid="filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9 bg-charcoal-800 border-[#E2D4C1] rounded-none focus-visible:ring-gold" />
                </div>
            </div>
            <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#6B645C] mb-2">Sort</div>
                <select data-testid="filter-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="w-full bg-charcoal-800 border border-[#E2D4C1] rounded-none px-3 py-2 text-sm focus:outline-none focus:border-gold">
                    <option value="">Featured</option>
                    <option value="price_asc">Price: Low to High</option>
                    <option value="price_desc">Price: High to Low</option>
                    <option value="name">Name A–Z</option>
                </select>
            </div>
            {brands.length > 0 && (
                <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-[#6B645C] mb-2">Brand</div>
                    <select data-testid="filter-brand" value={brand} onChange={(e) => setBrand(e.target.value)} className="w-full bg-charcoal-800 border border-[#E2D4C1] rounded-none px-3 py-2 text-sm focus:outline-none focus:border-gold">
                        <option value="">All Brands</option>
                        {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                </div>
            )}
            {types.length > 0 && (
                <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-[#6B645C] mb-2">Product Type</div>
                    <select data-testid="filter-type" value={type} onChange={(e) => setType(e.target.value)} className="w-full bg-charcoal-800 border border-[#E2D4C1] rounded-none px-3 py-2 text-sm focus:outline-none focus:border-gold capitalize">
                        <option value="">All Types</option>
                        {types.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>
            )}
            <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#6B645C] mb-2">Stock Status</div>
                <select data-testid="filter-stock" value={stock} onChange={(e) => setStock(e.target.value)} className="w-full bg-charcoal-800 border border-[#E2D4C1] rounded-none px-3 py-2 text-sm focus:outline-none focus:border-gold">
                    <option value="">Any</option>
                    {STOCK_OPTIONS.map((s) => <option key={s} value={s}>{STOCK_BADGES[s].label}</option>)}
                </select>
            </div>
            <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#6B645C] mb-2">Max Price</div>
                <Input data-testid="filter-max-price" type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="$" className="bg-charcoal-800 border-[#E2D4C1] rounded-none focus-visible:ring-gold" />
            </div>
            <label className="flex items-center gap-2 text-sm text-[#33302C] cursor-pointer">
                <input data-testid="filter-recommended" type="checkbox" checked={recommended} onChange={(e) => setRecommended(e.target.checked)} className="accent-[#8C5A3C]" /> MBG Recommended only
            </label>
            <button onClick={clearAll} className="text-xs uppercase tracking-widest text-gold hover:text-gold-hover inline-flex items-center gap-1"><X size={12} /> Clear filters</button>
        </div>
    );

    return (
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
            {/* Breadcrumb */}
            <nav data-testid="breadcrumb" className="flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase text-[#6B645C] mb-6 flex-wrap">
                <Link to="/" className="hover:text-gold">Home</Link>
                <ChevronRight size={12} />
                <Link to={topPath} className="hover:text-gold">{topLabel}</Link>
                <ChevronRight size={12} />
                <span className="text-[#1A1A1A]">{category.name}</span>
            </nav>

            <div className="mb-8">
                <h1 className="font-display text-4xl md:text-5xl tracking-tight mb-2">{category.name}</h1>
                <p className="text-[#33302C] max-w-2xl">{category.desc}</p>
            </div>

            <button onClick={() => setShowFilters(!showFilters)} className="lg:hidden mb-5 inline-flex items-center gap-2 px-4 py-2 border border-[#E2D4C1] text-[#33302C] uppercase tracking-widest text-xs" data-testid="toggle-filters">
                <SlidersHorizontal size={14} /> Filters
            </button>

            <div className="grid lg:grid-cols-[260px_1fr] gap-10">
                <aside className={`${showFilters ? "block" : "hidden"} lg:block`}>
                    <div className="lg:sticky lg:top-28 bg-[#F7F3EB] border border-[#E2D4C1] p-6">{filterBody}</div>
                </aside>

                <div>
                    <div className="text-xs text-[#6B645C] mb-5">{filtered.length} product{filtered.length !== 1 ? "s" : ""}</div>
                    {loading ? (
                        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
                            {[...Array(6)].map((_, i) => <div key={i} className="bg-charcoal-800 border border-[#E2D4C1] animate-pulse h-80" />)}
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="border border-[#E2D4C1] bg-[#F7F3EB] py-24 text-center" data-testid="empty-state">
                            <div className="font-display text-2xl mb-2">Coming Soon</div>
                            <p className="text-[#6B645C] text-sm">Products for this category are being added. Check back shortly or request a quote.</p>
                        </div>
                    ) : (
                        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="product-grid">
                            {filtered.map((p, i) => (
                                <ProductCard key={p.id} product={p} index={i} onQuickView={setQuickView} onRequestQuote={setQuoteFor} />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <ProductQuickView product={quickView} open={!!quickView} onOpenChange={(v) => !v && setQuickView(null)} onRequestQuote={setQuoteFor} />
            <RequestQuoteModal product={quoteFor} open={!!quoteFor} onOpenChange={(v) => !v && setQuoteFor(null)} />
        </div>
    );
}
